import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile, fetchSignInMethodsForEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { backendConfig } from '../config/backendConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';
import PremiumLoader from '../components/PremiumLoader';
import PasswordInput from '../components/PasswordInput';

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, isAuthenticated } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const completeProfile = searchParams.get('completeProfile') === 'true';
  
  // Redirect authenticated users with accounts to my-art-world (unless completing profile)
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    if (completeProfile) {
      // User is completing profile - check if they already have an artist profile
      const checkProfile = async () => {
        try {
          const profileSnap = await getDoc(doc(db, 'artistProfiles', user.uid));
          if (profileSnap.exists()) {
            // Profile already exists, redirect to artist wall
            console.log('Signup page - Artist profile already exists, redirecting to my-art-world');
            navigate('/my-art-world', { replace: true });
          } else {
            // No profile exists, redirect to artist profile creation page
            console.log('Signup page - No artist profile found, redirecting to artist-profile');
            navigate('/artist-profile', { replace: true });
          }
        } catch (error) {
          console.error('Error checking artist profile:', error);
          // On error, still redirect to profile creation
          navigate('/artist-profile', { replace: true });
        }
      };
      checkProfile();
      return;
    }

    // On /signup during the multi-step wizard, Firebase becomes authenticated mid-submit — do not
    // redirect to my-art-world (that caused a race and a flash of step 1 / wrong page).
    if (location.pathname === '/signup') return;

    if (user.accountType === 'student') {
      console.log('Signup page - Authenticated student not on signup wizard, redirecting to my-art-world');
      navigate('/my-art-world', { replace: true });
    }
  }, [isAuthenticated, user, navigate, completeProfile, location.pathname]);
  
  // Step management (account type step hidden — all signups are individual artists for now)
  const [currentStep, setCurrentStep] = useState(1);
  const accountType = 'student';
  const mentorType = '';
  
  // Common fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Student-specific fields
  const [dob, setDob] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  
  // Mentor-specific fields
  const [mentorName, setMentorName] = useState('');
  const [mentorCount, setMentorCount] = useState(1);
  
  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [progressStep, setProgressStep] = useState('');
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [dobError, setDobError] = useState('');
  
  // Calculate age for students
  const age = React.useMemo(() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const diff = Date.now() - d.getTime();
    return new Date(diff).getUTCFullYear() - 1970;
  }, [dob]);

  const handleDobSelect = (date) => {
    if (!date) {
      setDob('');
      setDobError('Please select a valid date.');
      return;
    }
    const iso = date.toISOString().slice(0, 10); // YYYY-MM-DD
    setDob(iso);
    setDobError('');
    setDobPickerOpen(false);
  };
  
  const isUnder13 = accountType === 'student' && age !== null && age < 13;

  const handleNext = () => {
    setError('');
    
    if (currentStep === 1) {
      // Validate basic information
      if (!name || !email || !password || !confirmPassword) {
        setError('Please fill in all required fields.');
        return;
      }
      
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      
      if (password.length < 6) {
        setError('Password must be at least 6 characters long.');
        return;
      }
      
      if (accountType === 'student' && !dob) {
        setError('Please enter your date of birth.');
        return;
      }
      
      if (isUnder13 && !parentEmail) {
        setError('Parent/guardian email is required for students under 13.');
        return;
      }
      
      
      if (accountType === 'mentor' && !mentorName) {
        setError('Please enter your mentor name.');
        return;
      }
      
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/');
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  // Check if email exists for specific account type
  const checkEmailAccountType = async (email, accountType, mentorType) => {
    try {
      // Skip email check for single mentors to prevent timeout issues
      if (accountType === 'mentor' && mentorType === 'single') {
        console.log('Skipping email check for single mentor to prevent timeout issues');
        return { ok: true, canProceed: true };
      }
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // Increased to 10 second timeout
      
      const response = await fetch(`${backendConfig.baseUrl}/api/membership-public/check-email-account-type?email=${encodeURIComponent(email)}&accountType=${accountType}&mentorType=${mentorType || ''}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return { ok: true, canProceed: true }; // Allow if check fails
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.info('Email account type check aborted; continuing signup.');
      } else {
        console.error('Error checking email account type:', error);
      }
      return { ok: true, canProceed: true }; // Allow if check fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    /** When true, skip `finally` so the full-screen overlay stays up until `navigate` unmounts this page. */
    let keepLoadingOverlayUntilNavigate = false;

    // Set loading state immediately for instant feedback
    setLoading(true);
    setError('');
    setSuccess('');
    setProgressStep('Validating your information...');

    try {
      // First check if email exists for this account type
      console.log('Checking email account type...', { email, accountType, mentorType });
      const emailCheck = await checkEmailAccountType(email, accountType, mentorType);
      console.log('Email check result:', emailCheck);
      if (!emailCheck.canProceed) {
        setError(emailCheck.message);
        setLoading(false);
        setProgressStep('');
        return;
      }

      // If email exists with different account type, allow signup (user will get account selector during login)
      // No need to block signup - the account type selector will handle login routing

      // Network connectivity test removed - Firebase Auth will handle network errors
      // The previous test was causing CORS errors with Identity Toolkit API

      // Create Firebase user with retry
      console.log('Creating Firebase user...');
      setProgressStep('Creating your account...');
      let userCredential;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          console.log(`Attempting Firebase Auth (attempt ${retryCount + 1}/${maxRetries + 1})...`);
          
          // Add timeout to Firebase Auth call (reduced timeout)
          const authPromise = createUserWithEmailAndPassword(auth, email, password);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Firebase Auth timeout')), 5000)
          );
          
          userCredential = await Promise.race([authPromise, timeoutPromise]);
          console.log('Firebase user created successfully:', userCredential.user.uid);
          break; // Success, exit retry loop
        } catch (firebaseError) {
          console.error(`Firebase Auth error (attempt ${retryCount + 1}):`, firebaseError);
          console.error('Error code:', firebaseError.code);
          console.error('Error message:', firebaseError.message);
          
          if (firebaseError.code === 'auth/network-request-failed' || firebaseError.message === 'Firebase Auth timeout') {
            if (retryCount < maxRetries) {
              console.log(`Retrying in 2 seconds... (attempt ${retryCount + 1}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, 2000));
              retryCount++;
              continue;
            } else {
              setError('Network connection failed after multiple attempts. Please try again in a few minutes or contact support if the issue persists.');
              setLoading(false);
              return;
            }
          }
          throw firebaseError; // Re-throw other errors
        }
      }
      const user = userCredential.user;

      // Update display name
      console.log('Updating profile...');
      setProgressStep('Setting up your profile...');
      await updateProfile(user, {
        displayName: name
      });
      console.log('Profile updated');

      // Create user document in Firestore
      console.log('Creating Firestore document...');
      setProgressStep('Saving your information...');
      const userData = {
        name,
        email,
        accountType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (accountType === 'student') {
        userData.needsStudioOnboarding = true;
        userData.onboardingCompleted = false;
        userData.bookCustomerStatus = 'unknown';
      }

      // Add account-specific data
      if (accountType === 'student') {
        userData.dob = dob;
        userData.age = age;
        userData.isUnder13 = isUnder13; // Save isUnder13 flag so Cloud Function can detect it
        if (isUnder13) {
          userData.parentEmail = parentEmail;
        }
      } else if (accountType === 'mentor') {
        userData.mentorName = mentorName;
        userData.mentorType = mentorType; // 'organization' or 'single'
        if (mentorType === 'organization') {
          userData.isPrimaryId = true; // Mark as primary organization account
          userData.mentorCount = mentorCount; // Save mentor count for organizations
        }
        // Access code will be generated after purchase for both types
      }

      await setDoc(doc(db, 'users', user.uid), userData);
      console.log('Firestore document created');

      // Login with the new user
      console.log('Logging in user...');
      setProgressStep('Logging you in...');
      await login(user);
      console.log('User logged in');

      // Prevent route-guard redirects during the immediate post-signup window.
      // ProtectedRoute uses this grace period to keep the onboarding journey seamless.
      try {
        localStorage.setItem('membershipJustActivatedAt', String(Date.now()));
        localStorage.setItem('membershipJustActivatedPlan', 'studio-onboarding');
      } catch (_) {}

      setSuccess('Account created successfully! Welcome to Artmosphere!');
      setProgressStep(
        accountType === 'mentor'
          ? 'Taking you to membership...'
          : 'Taking you to studio onboarding...'
      );
      console.log('Success message set');

      keepLoadingOverlayUntilNavigate = true;

      // Redirect based on account type — defer one tick so the overlay paints before navigation.
      console.log('Setting up redirect...', { accountType, mentorType });
      setTimeout(() => {
        console.log('Executing redirect...');
        if (accountType === 'mentor' && mentorType === 'organization') {
          // Organization mentors: Go directly to membership with mentor count
          console.log('Redirecting to membership for organization with mentor count:', mentorCount);
          navigate('/membership', { 
            state: { 
              isNewOrganization: true,
              mentorCount: mentorCount,
              userData: { uid: user.uid, email: user.email }
            } 
          });
        } else if (accountType === 'mentor' && mentorType === 'single') {
          // Single mentors: Go directly to membership selection (no separate mentor setup needed)
          console.log('Redirecting to membership for single mentor');
          navigate('/membership', { 
            state: { 
              isNewSingleMentor: true,
              userData: { uid: user.uid, email: user.email }
            }
          });
        } else {
          console.log('Redirecting to studio onboarding for student');
          navigate('/onboarding/studio-access', { replace: true });
        }
      }, 0);

    } catch (error) {
      console.error('Signup error:', error);
      setProgressStep('');
      if (error.name === 'AbortError') {
        setError('Request timed out. Please check your internet connection and try again.');
      } else if (error.code === 'auth/email-already-in-use') {
        try {
          // Extra verification: does Firebase actually have sign-in methods? If yes, guide or sign-in.
          const methods = await fetchSignInMethodsForEmail(auth, email);
          console.log('Existing Firebase sign-in methods for email:', methods);
          
          if (methods && methods.length > 0) {
            // Account actually exists - try to sign in
            try {
              const loginCred = await signInWithEmailAndPassword(auth, email, password);
              await login(loginCred.user);
              keepLoadingOverlayUntilNavigate = true;
              setProgressStep('Taking you to your dashboard...');
              // Redirect like successful signup
              navigate(accountType === 'mentor' && mentorType === 'single' ? '/membership' : accountType === 'mentor' ? '/mentor-setup' : '/dashboard', {
                state: accountType === 'mentor' && mentorType === 'single'
                  ? { isNewSingleMentor: true, userData: { uid: loginCred.user.uid, email: loginCred.user.email } }
                  : accountType === 'mentor'
                  ? { isNewOrganization: true, userData: { uid: loginCred.user.uid, email: loginCred.user.email } }
                  : undefined
              });
              return;
            } catch (signinErr) {
              console.warn('Auto sign-in after email-in-use failed:', signinErr);
              setError('This email is already registered. Please sign in instead or use a different email address.');
            }
          } else {
            // Firebase bug: No methods but claims email-in-use
            console.warn('Firebase bug detected: email-already-in-use but no sign-in methods found');
            console.log('Attempting workaround: retry with fresh Firebase instance...');
            
            // Workaround: Try one more time with a small delay
            try {
              await new Promise(resolve => setTimeout(resolve, 2000));
              const retryCredential = await createUserWithEmailAndPassword(auth, email, password);
              console.log('Workaround successful:', retryCredential.user.uid);
              
              // Continue with normal flow
              const user = retryCredential.user;
              await updateProfile(user, { displayName: name });
              
              const userData = {
                name,
                email,
                accountType,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              };
              
              if (accountType === 'student') {
                userData.needsStudioOnboarding = true;
                userData.onboardingCompleted = false;
                userData.bookCustomerStatus = 'unknown';
              }
              
              if (accountType === 'student') {
                userData.dob = dob;
                userData.age = age;
                userData.isUnder13 = isUnder13; // Save isUnder13 flag so Cloud Function can detect it
                if (isUnder13) userData.parentEmail = parentEmail;
              } else if (accountType === 'mentor') {
                userData.mentorName = mentorName;
                userData.mentorType = mentorType;
                if (mentorType === 'organization') userData.isPrimaryId = true;
              }
              
              await setDoc(doc(db, 'users', user.uid), userData);
              await login(user);
              setSuccess('Account created successfully! Welcome to Artmosphere!');
              keepLoadingOverlayUntilNavigate = true;
              setProgressStep(
                accountType === 'mentor'
                  ? 'Taking you to membership...'
                  : 'Taking you to studio onboarding...'
              );

              setTimeout(() => {
                navigate(
                  accountType === 'mentor' && mentorType === 'single'
                    ? '/membership'
                    : accountType === 'mentor'
                      ? '/mentor-setup'
                      : '/onboarding/studio-access',
                  {
                    replace: true,
                    state:
                      accountType === 'mentor' && mentorType === 'single'
                        ? { isNewSingleMentor: true, userData: { uid: user.uid, email: user.email } }
                        : accountType === 'mentor'
                          ? { isNewOrganization: true, userData: { uid: user.uid, email: user.email } }
                          : undefined,
                  }
                );
              }, 0);
              return;
              
            } catch (retryError) {
              console.error('Workaround failed:', retryError);
              if (retryError.code === 'auth/email-already-in-use') {
                setError('This email is already registered. Please sign in instead or use a different email address.');
              } else {
                setError('We encountered a temporary issue. Please try again in a few seconds.');
              }
            }
          }
        } catch (methodsErr) {
          console.warn('Could not verify sign-in methods:', methodsErr);
          setError('This email is already registered. Please sign in instead or use a different email address.');
        }
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Unable to create account. Please check your details and try again.');
      }
    } finally {
      if (!keepLoadingOverlayUntilNavigate) {
        setLoading(false);
        setProgressStep('');
      }
    }
  };

  return (
    <div className="art-premium-page-bg relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#F5E2C8]/35 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-[#FFF0DC]/40 blur-3xl" />
      <div className="relative z-10 p-3 sm:p-6 md:p-8">
        <div className="art-premium-surface mx-auto max-w-5xl p-4 sm:p-6 md:p-8">
        {/* Header with Logo and Navigation */}
        <ArtmosphereNavbar
          title="Artmosphere"
          subtitle="Join the creative community"
          embedded
        />

        {/* Welcome Message */}
        <div className="signup-page-welcome text-center mb-6 sm:mb-8">
          <h2 className="art-premium-heading mb-2 text-2xl sm:text-4xl">Welcome to Artmosphere!</h2>
          <p className="art-premium-body-text mx-auto max-w-2xl text-sm sm:text-lg px-1">
            Join thousands of artists and creative minds in the world&apos;s most vibrant art community.
            Start your creative journey today.
          </p>
        </div>

        {/* Progress — individual artist signup (2 steps) */}
        <div className="signup-progress art-premium-surface">
          <div className="signup-progress__row" role="list" aria-label="Signup progress">
            <div className="signup-progress__step" role="listitem">
              <div className={`signup-progress__dot ${currentStep >= 1 ? 'signup-progress__dot--active' : ''}`}>1</div>
              <span className={`signup-progress__label ${currentStep >= 1 ? 'signup-progress__label--active' : ''}`}>
                <span className="sm:hidden">Info</span>
                <span className="hidden sm:inline">Basic Info</span>
              </span>
            </div>
            <div className="signup-progress__connector" aria-hidden="true">
              <div className={`signup-progress__connector-fill ${currentStep >= 2 ? 'signup-progress__connector-fill--done' : ''}`} />
            </div>
            <div className="signup-progress__step" role="listitem">
              <div className={`signup-progress__dot ${currentStep >= 2 ? 'signup-progress__dot--active' : ''}`}>2</div>
              <span className={`signup-progress__label ${currentStep >= 2 ? 'signup-progress__label--active' : ''}`}>
                <span className="sm:hidden">Done</span>
                <span className="hidden sm:inline">Complete</span>
              </span>
            </div>
          </div>
        </div>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="signup-step-panel art-premium-surface p-4 sm:p-6">
            <h2 className="art-premium-heading mb-3 text-xl sm:text-2xl">Basic Information</h2>
            <p className="art-premium-body-text mb-4 sm:mb-6 text-sm sm:text-base">
              Create your individual artist account — tell us a bit about yourself to get started.
            </p>

            <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="auth-premium-label mb-1 block">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="auth-premium-input px-3 py-2"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label className="auth-premium-label mb-1 block">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-premium-input px-3 py-2"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="auth-premium-label mb-1 block">Password</label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-premium-input px-3 py-2"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div>
                  <label className="auth-premium-label mb-1 block">Confirm Password</label>
                  <PasswordInput
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="auth-premium-input px-3 py-2"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              {/* Student-specific fields */}
              {accountType === 'student' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                  <div className="relative">
                    <label className="auth-premium-label mb-1 block">Date of Birth</label>
                    {/* Custom DOB input with formatted display */}
                    <button
                      type="button"
                      onClick={() => setDobPickerOpen(true)}
                      className="auth-premium-input flex w-full items-center justify-between px-3 py-2 text-left"
                    >
                      <span className={dob ? 'text-slate-900' : 'text-slate-400'}>
                        {dob
                          ? (() => {
                              const d = new Date(dob);
                              if (Number.isNaN(d.getTime())) return dob;
                              const dd = String(d.getDate()).padStart(2, '0');
                              const mm = String(d.getMonth() + 1).padStart(2, '0');
                              const yyyy = d.getFullYear();
                              return `${dd}/${mm}/${yyyy}`;
                            })()
                          : 'Select your date of birth'}
                      </span>
                      <span className="text-amber-500 text-sm">📅</span>
                    </button>
                    {dobError && (
                      <p className="mt-1 text-xs text-red-600">{dobError}</p>
                    )}
                    {age !== null && (
                      <div className="flex items-center gap-2 text-xs mt-1">
                        <div className={`w-2 h-2 rounded-full ${age < 13 ? 'bg-amber-400' : 'bg-green-400'}`}></div>
                        <span className="text-slate-600">
                          Age: <span className="font-semibold text-slate-800">{age}</span>
                          {age < 13 && <span className="text-[#8b6914] ml-1">(Parent consent required)</span>}
                        </span>
                      </div>
                    )}

                    {/* Lightweight DOB picker modal */}
                    {dobPickerOpen && (
                      <div className="art-premium-surface absolute bottom-full left-0 z-40 mb-2 w-full max-w-xs rounded-xl p-3 shadow-xl">
                        <DobCalendar
                          value={dob}
                          onSelect={handleDobSelect}
                          onClose={() => setDobPickerOpen(false)}
                        />
                      </div>
                    )}
                  </div>
                  {isUnder13 && (
                    <div>
                      <label className="auth-premium-label mb-1 block">Parent/Guardian Email</label>
                      <input
                        type="email"
                        value={parentEmail}
                        onChange={(e) => setParentEmail(e.target.value)}
                        className="auth-premium-input px-3 py-2"
                        placeholder="parent@email.com"
                        required={isUnder13}
                      />
                    </div>
                  )}
                </div>
              )}


              {/* Mentor-specific fields */}
              {accountType === 'mentor' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="auth-premium-label mb-1 block">
                      {mentorType === 'organization' ? 'Organization Name' : 'Mentor Name'}
                    </label>
                    <input
                      type="text"
                      value={mentorName}
                      onChange={(e) => setMentorName(e.target.value)}
                      className="auth-premium-input px-3 py-2"
                      placeholder={mentorType === 'organization' ? 'Your organization name' : 'Your mentor name'}
                      required
                    />
                  </div>
                  
                  {/* Mentor count for organizations */}
                  {mentorType === 'organization' && (
                    <div>
                      <label className="auth-premium-label mb-1 block">Number of Mentors</label>
                      <select
                        value={mentorCount}
                        onChange={(e) => setMentorCount(parseInt(e.target.value))}
                        className="auth-premium-input px-3 py-2"
                        required
                      >
                        {Array.from({ length: 20 }, (_, i) => i + 1).map((count) => (
                          <option key={count} value={count}>
                            {count} {count === 1 ? 'Mentor' : 'Mentors'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="signup-step-actions">
                <button
                  type="button"
                  onClick={handleBack}
                  className="art-btn-secondary signup-step-btn signup-step-btn--secondary transition-colors"
                >
                  <span className="sm:hidden">Back</span>
                  <span className="hidden sm:inline">Back to Artist Corner</span>
                </button>
                <button
                  type="submit"
                  className="art-btn-primary signup-step-btn"
                >
                  <span className="sm:hidden">Continue</span>
                  <span className="hidden sm:inline">Next: Complete Account</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Complete Account */}
        {currentStep === 2 && (
          <div className="art-premium-surface overflow-hidden p-4 sm:p-6">
            <h2 className="art-premium-heading mb-3 text-xl sm:mb-4 sm:text-2xl">Complete Your Account</h2>
            <p className="text-sm sm:text-base text-[#6b5a48] mb-4 sm:mb-6">Review your information and complete your account setup.</p>

            {/* Account Summary */}
            <div className="art-callout-strip p-4 sm:p-6 mb-4 sm:mb-6 overflow-x-auto">
              <h3 className="art-premium-heading mb-3 text-base sm:mb-4 sm:text-lg">Account Summary</h3>
              <div className="space-y-2.5 sm:space-y-3 min-w-0">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-[#8b6914] flex-shrink-0">Account:</span>
                  <span className="font-medium text-xs sm:text-sm text-[#2f251b] break-words">Individual Artist</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-[#8b6914] flex-shrink-0">Name:</span>
                  <span className="font-medium text-xs sm:text-sm text-[#2f251b] break-words">{name}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-[#8b6914] flex-shrink-0">Email:</span>
                  <span className="font-medium text-xs sm:text-sm text-[#2f251b] break-all">{email}</span>
                </div>
                {accountType === 'student' && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-[#8b6914] flex-shrink-0">Age:</span>
                    <span className="font-medium text-xs sm:text-sm text-[#2f251b]">{age}</span>
                  </div>
                )}
                {accountType === 'mentor' && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-[#8b6914] flex-shrink-0">Mentor Type:</span>
                    <span className="font-medium text-xs sm:text-sm text-[#2f251b] capitalize break-words">{mentorType}</span>
                  </div>
                )}
                {accountType === 'mentor' && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-[#8b6914] flex-shrink-0">
                      {mentorType === 'organization' ? 'Organization Name:' : 'Mentor Name:'}
                    </span>
                    <span className="font-medium text-xs sm:text-sm text-[#2f251b] break-words">{mentorName}</span>
                  </div>
                )}
                {accountType === 'mentor' && mentorType === 'organization' && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-[#8b6914] flex-shrink-0">Number of Mentors:</span>
                    <span className="font-medium text-xs sm:text-sm text-[#2f251b]">{mentorCount}</span>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg mb-4 sm:mb-6">
                <p className="text-xs sm:text-sm text-red-700 break-words">{error}</p>
              </div>
            )}

            {success && (
              <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg mb-4 sm:mb-6">
                <p className="text-xs sm:text-sm text-green-700 break-words">{success}</p>
              </div>
            )}

            <div className="signup-step-actions">
              <button
                type="button"
                onClick={handleBack}
                className="art-btn-secondary signup-step-btn signup-step-btn--secondary transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="art-btn-primary signup-step-btn disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (progressStep || 'Creating…') : 'Create Account'}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {loading && (
        <PremiumLoader
          variant="overlay"
          message={progressStep || 'Creating your account…'}
          subtitle="Please stay on this page"
        />
      )}
    </div>
  );
}

function DobCalendar({ value, onSelect, onClose }) {
  const initial = value ? new Date(value) : new Date();
  const [viewDate, setViewDate] = useState(
    Number.isNaN(initial.getTime()) ? new Date() : initial
  );

  const startOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startDay = startOfMonth.getDay(); // 0 (Sun) - 6 (Sat)
  const daysInMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() + 1,
    0
  ).getDate();

  const weeks = [];
  let day = 1 - startDay;
  for (let w = 0; w < 6; w += 1) {
    const week = [];
    for (let d = 0; d < 7; d += 1) {
      if (day < 1 || day > daysInMonth) {
        week.push(null);
      } else {
        week.push(day);
      }
      day += 1;
    }
    weeks.push(week);
  }

  const handleDayClick = (dayNum) => {
    if (!dayNum) return;
    const selected = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      dayNum
    );
    onSelect(selected);
  };

  const monthLabel = viewDate.toLocaleString('default', { month: 'short' });
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= currentYear - 80; y -= 1) {
    years.push(y);
  }

  return (
    <div className="text-xs text-slate-800">
      <div className="flex items-center justify-between mb-2 gap-2">
        <button
          type="button"
          className="auth-premium-close rounded p-1"
          onClick={() =>
            setViewDate(
              new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1)
            )
          }
        >
          ‹
        </button>
        <div className="flex items-center gap-2">
          <div className="font-semibold">{monthLabel}</div>
          <select
            className="auth-premium-input rounded border px-1 py-0.5 text-[11px]"
            value={viewDate.getFullYear()}
            onChange={(e) =>
              setViewDate(
                new Date(parseInt(e.target.value, 10), viewDate.getMonth(), 1)
              )
            }
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="auth-premium-close rounded p-1"
          onClick={() =>
            setViewDate(
              new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1)
            )
          }
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] text-slate-500">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {weeks.map((week, wi) =>
          week.map((dayNum, di) => {
            if (!dayNum) {
              return <div key={`${wi}-${di}`} />;
            }
            const isSelected =
              value &&
              (() => {
                const cur = new Date(value);
                return (
                  !Number.isNaN(cur.getTime()) &&
                  cur.getDate() === dayNum &&
                  cur.getMonth() === viewDate.getMonth() &&
                  cur.getFullYear() === viewDate.getFullYear()
                );
              })();
            return (
              <button
                type="button"
                key={`${wi}-${di}`}
                onClick={() => handleDayClick(dayNum)}
                className={`h-8 w-8 rounded-full text-center text-xs leading-8 ${
                  isSelected
                    ? 'auth-premium-progress-active text-[#5c4428]'
                    : 'text-slate-800 hover:bg-[#FFF9EF]'
                }`}
              >
                {dayNum}
              </button>
            );
          })
        )}
      </div>
      <div className="flex justify-between mt-2">
        <button
          type="button"
          className="art-btn-secondary rounded border px-2 py-1 text-[11px]"
          onClick={() => {
            const today = new Date();
            onSelect(today);
          }}
        >
          Today
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded text-[11px] text-slate-500 hover:bg-slate-50"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}

